import { useQuery } from '@tanstack/react-query';
import { http } from '@/lib/http';
import { useLocale } from '@/app/[lang]/providers/LocaleProvider';

export interface HeroSliderItem {
  id: number;
  title?: string | null;
  title_font_size: number | null;
  desc?: string | null;
  desc_font_size: number | null;
  image: string;
  image_alt: string;
  video?: string | null;
  video_type?: string | null;
  /**
   * Optional lighter encode of `video` (e.g. 720p) the CMS can provide for
   * small screens and slow connections. Missing -> `video` is used as-is.
   */
  video_mobile?: string | null;
  cta_url?: string | null;
  cta_text?: string | null;
  content_position_layout?: 'left' | 'center' | 'right' | null;
  content_position_location?: 'top' | 'center' | 'bottom' | 'middle' | null;
  content_position?: 'left' | 'right' | 'center' | 'centered' | 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | null;
  deleted_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface HeroSliderResponse {
  data: HeroSliderItem[];
}

/**
 * Hero videos/images live on a separate host (MinIO). Warm up DNS + TLS for
 * that origin as soon as the CMS answers, so the first media request doesn't
 * pay the handshake cost. Browser-reusable afterwards; already-added origins
 * are skipped, so this is safe to run on every fetch.
 */
function preconnectMediaOrigins(items?: HeroSliderItem[]) {
  if (typeof document === "undefined" || !items?.length) return;

  for (const item of items) {
    for (const url of [item.video, item.image]) {
      if (!url) continue;
      try {
        const { origin } = new URL(url);
        if (document.head.querySelector(`link[rel="preconnect"][href="${origin}"]`)) continue;

        const link = document.createElement("link");
        link.rel = "preconnect";
        link.href = origin;
        document.head.appendChild(link);
      } catch {
        // ignore malformed URLs coming from the CMS
      }
    }
  }
}

export function useHeroSlider() {
  const lang = useLocale();
  return useQuery<HeroSliderResponse>({
    queryKey: ['hero-slider', lang],
    enabled: true,
    queryFn: async () => {
      const res = await http.get(`/api/hero-sliders?lang=${lang}`);
      preconnectMediaOrigins(res.data?.data);
      return res.data;
    },
  });
}
